// /client/src/components/StatutReglementsDiagramme.js
import React, { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

/* Palette par statut (normalisée sans accents, en minuscules) */
const COLOR_MAP = {
  "regle": "#10B981",                 // Réglé
  "non regle": "#F97316",             // Non Réglé / Non réglé
  "en cours": "#F59E0B",              // En cours
  "en attente": "#F59E0B",            // En attente
  "probleme": "#EF4444",              // Problème
  "annule": "#475569",                // Annulé
  "partiellement regle": "#14B8A6",   // Partiellement Réglé
};

const FALLBACK_COLORS = [
  "#6366F1", "#22C55E", "#F59E0B", "#EF4444",
  "#0EA5E9", "#A855F7", "#14B8A6", "#84CC16",
];

const normalize = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const colorFor = (label, idx) =>
  COLOR_MAP[normalize(label)] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

/* Tooltip custom (dark-aware) */
function CustomTooltip({ active, payload, isDark }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const name = p?.name ?? "";
  const value = p?.value ?? 0;

  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-sm ${
        isDark
          ? "bg-slate-900 border-slate-700 text-slate-100"
          : "bg-white border-slate-200 text-slate-800"
      }`}
    >
      <div className="text-[11px] opacity-70">{name}</div>
      <div className="text-sm font-semibold">{Number(value).toFixed(1)}%</div>
    </div>
  );
}

export default function StatutReglementsDiagramme({ data = [] }) {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  const prepared = useMemo(() => {
    const safe = Array.isArray(data) ? data : [];
    return safe.map((d, i) => ({
      name: d.statut ?? d.name ?? "—",
      value: Number.parseFloat(d.percent ?? d.value ?? 0) || 0,
      color: colorFor(d.statut ?? d.name ?? "", i),
    }));
  }, [data]);

  const total = prepared.reduce((s, x) => s + x.value, 0);
  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.15)";

  if (!prepared.length) {
    return (
      <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-5 text-center">
        <div className="text-sm text-slate-600 dark:text-slate-300">
          Aucune donnée à afficher.
        </div>
      </div>
    );
  }

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête sobre + accent */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Répartition des statuts de règlements
        </h3>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      <div className="px-5 pb-5 sm:px-6 sm:pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          {/* Donut */}
          <div className="w-full h-[320px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  cursor={{ fill: cursorFill }}
                  content={<CustomTooltip isDark={isDark} />}
                />
                <Pie
                  data={prepared}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={70}
                  outerRadius={120}
                  paddingAngle={2}
                  stroke={isDark ? "#0f172a" : "#ffffff"}
                  strokeWidth={2}
                  isAnimationActive
                  label={({ name, value }) =>
                    value >= 6 ? `${name} ${Number(value).toFixed(0)}%` : ""
                  }
                  labelStyle={{ fill: tick, fontSize: 11 }}
                >
                  {prepared.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Légende “barres %” modernisée */}
          <div className="w-full space-y-2">
            {prepared.map((item, i) => (
              <div key={i} className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-slate-50 dark:bg-slate-900/40">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full ring-1 ring-black/10"
                      style={{ backgroundColor: item.color }}
                      aria-hidden
                    />
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">
                    {item.value.toFixed(1)}%
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(item.value, 3)}%`,
                      background: `linear-gradient(90deg, ${item.color}, ${item.color}AA)`,
                    }}
                    aria-label={`${item.name}: ${item.value.toFixed(1)}%`}
                  />
                </div>
              </div>
            ))}

            {/* total (optionnel si != 100) */}
            {Math.round(total) !== 100 && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Total: {total.toFixed(1)}%
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
